import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AppFeatures } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import ProfileImage from 'components/ProfileImage/ProfileImage';
import Rating from 'components/Rating/Rating';
import config from 'config';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShopType } from 'types/shop.types';
import { UserType } from 'types/user.types';
import ImageUrlResizer from 'utils/image-url-resizer';

import ShopUserProfileFeedbackActions from './ShopUserProfileActions';
import ShopUserProfileFeedbackDescription from './ShopUserProfileDescription';

type ShopProfile = {
  shop: ShopType;
  profileImage: string;
  lastActive?: string;
  likeCount: number;
  orderCount: string;
  currentUser: UserType;
  likeShop: () => void;
  refreshParentDataFn?: () => void;
  likeCallback?: () => void;
  onRatingClick?: () => void;
  imageRef?: (el: HTMLDivElement | null) => void;
};

const shopLikesAndSoldEnabled = config.get('stats.shopLikesAndSoldEnabled') || false;

const ShopUserProfileFeedback = (props: ShopProfile) => {
  const { shop, profileImage, likeCount, likeShop, orderCount, lastActive, currentUser, likeCallback, onRatingClick, imageRef } = props;
  const { t } = useTranslation();

  const isLoggedIn = currentUser?.isLoggedIn;
  const isFollowingShop = Boolean(shop?.owner?.hasLiked);

  // Ensures GrowthBook loads this feature flag for experiment tracking, even though the logic is handled in the backend
  useFeatureIsOn<AppFeatures>('multilang_shop_descriptions_enabled');

  const resizedProfileImage = useMemo(() => ImageUrlResizer.resize(profileImage, 300), [profileImage]);

  return (
    <>
      <div className="shop-user-profile-feedback">
        <div ref={imageRef}>
          <ProfileImage src={resizedProfileImage} className="shop-user-profile-feedback__image" />
        </div>

        {/* SHOP NAME */}
        <div className="shop-user-profile-feedback__info">
          <div className="shop-user-profile-feedback__name">{shop?.name}</div>

          {/* RATING */}
          <Rating feedbacks={shop?.feedbacks} onClick={onRatingClick} />

          {/* SHOP LIKES & SOLD */}
          {(shopLikesAndSoldEnabled || shop?.owner_id === currentUser?.id) && (
            <div className="shop-user-profile-feedback__stats">
              {orderCount && (
                <div className="shop-user-profile-feedback__stat">
                  <div className="shop-user-profile-feedback__stat-value">{orderCount}</div>
                  <div className="shop-user-profile-feedback__stat-label">{t('components.page-shop.items-sold')}</div>
                </div>
              )}

              <div className="shop-user-profile-feedback__stat" role="button" tabIndex={0} onClick={likeShop}>
                <div className="shop-user-profile-feedback__stat-value">{likeCount}</div>
                <div className="shop-user-profile-feedback__stat-label">{t('components.page-shop.followers', { count: likeCount })}</div>
              </div>
            </div>
          )}

          <div className="desktop">
            {shop?.owner_id !== currentUser?.id && (
              <ShopUserProfileFeedbackActions
                isFollowingShop={isFollowingShop}
                likeCallback={likeCallback}
                isLoggedIn={isLoggedIn}
                activeSlug={shop.activeSlug}
                lastActive={lastActive}
                showLocation={Boolean(shop.province || shop.location)}
                province={shop.province}
                location={shop.location}
                instagramTag={shop.instagramTag}
                formattedOrderProcessingTime={shop.formattedOrderProcessingTime}
                holidayMode={shop.holidayMode}
              />
            )}
            <ShopUserProfileFeedbackDescription description={shop.description} translatedDescription={shop.translatedDescription} />
          </div>
        </div>
      </div>
      <div className="mobile">
        {shop?.owner_id !== currentUser?.id && (
          <ShopUserProfileFeedbackActions
            isFollowingShop={isFollowingShop}
            likeCallback={likeCallback}
            isLoggedIn={isLoggedIn}
            activeSlug={shop.activeSlug}
            lastActive={lastActive}
            showLocation={Boolean(shop.province || shop.location)}
            province={shop.province}
            location={shop.location}
            instagramTag={shop.instagramTag}
            formattedOrderProcessingTime={shop.formattedOrderProcessingTime}
            holidayMode={shop.holidayMode}
          />
        )}
        <ShopUserProfileFeedbackDescription description={shop.description} translatedDescription={shop.translatedDescription} />
      </div>
    </>
  );
};

export default ShopUserProfileFeedback;
